import React, { useState, useEffect, useRef } from "react";

import { useHistory } from "react-router-dom";
import { parseISO, format, isSameDay, differenceInHours, isYesterday, differenceInMinutes } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StyleIcon from '@mui/icons-material/Style';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import LinkIcon from '@mui/icons-material/Link';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Box, Tooltip, IconButton } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

import GroupIcon from '@material-ui/icons/Group';
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { isNil } from "lodash";
import { toast } from "sonner";
import { Done, HighlightOff, Replay, SwapHoriz } from "@material-ui/icons";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { Chip, Stack } from "@mui/material";

const useStyles = makeStyles((theme) => ({
    ticket: {
        position: "relative",
        minHeight: 82,
        height: "auto",
        padding: "10px 12px",
        borderRadius: 12,
        fontFamily: '"Inter", "Poppins", sans-serif',
        backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#FFFFFF",
        boxShadow: theme.palette.type === "dark"
            ? "0 1px 3px rgba(0, 0, 0, 0.3)"
            : "0 1px 3px rgba(0, 0, 0, 0.06)",
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "#263348" : "#F8FAFC",
            boxShadow: theme.palette.type === "dark"
                ? "0 2px 8px rgba(0, 0, 0, 0.4)"
                : "0 2px 8px rgba(0, 0, 0, 0.08)",
        },
        "&.Mui-selected": {
            backgroundColor: theme.palette.type === "dark" ? "#263348" : "#F0FDF9",
            borderLeft: "3px solid #0B3C84",
            "&:hover": {
                backgroundColor: theme.palette.type === "dark" ? "#2D3D52" : "#E6FAF5",
            },
        },
        marginBottom: 4,
    },

    pendingTicket: {
        cursor: "unset",
    },

    newMessagesCount: {
        justifySelf: "flex-end",
        textAlign: "right",
        position: "relative",
        top: 0,
        color: "#0B3C84",
        fontWeight: "bold",
        marginRight: "-16px",
        borderRadius: 0,
    },

    contactNameWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        marginLeft: 4,
        gap: 6,
    },

    contactName: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontWeight: 600,
        fontSize: "0.875rem",
        color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    },

    lastMessageTime: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        justifySelf: "flex-end",
        textAlign: "right",
        position: "relative",
        fontSize: "0.7rem",
        color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    },

    lastMessageTimeUnread: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        justifySelf: "flex-end",
        textAlign: "right",
        position: "relative",
        color: "#0B3C84",
        fontWeight: 600,
        fontSize: "0.7rem",
    },

    closedBadge: {
        alignSelf: "center",
        justifySelf: "flex-end",
        marginRight: 32,
        marginLeft: "auto",
    },

    contactLastMessage: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        paddingRight: 0,
        marginLeft: 4,
        fontSize: "0.8rem",
        color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    },

    contactLastMessageUnread: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        paddingRight: 20,
        fontWeight: 600,
        fontSize: "0.8rem",
        color: theme.palette.type === "dark" ? "#E2E8F0" : "#1E293B",
        width: "50%",
    },

    badgeStyle: {
        position: "flex",
        top: "1px",
        right: "-9.5px",
        color: "#FFFFFF",
        backgroundColor: "#0B3C84",
        borderRadius: "50%",
        padding: 5,
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontWeight: 600,
        fontSize: "0.65rem",
    },

    avatarContainer: {
        position: "relative",
    },

    avatar: {
        width: 48,
        height: 48,
        borderRadius: "50%",
        border: theme.palette.type === "dark"
            ? "2px solid rgba(255,255,255,0.1)"
            : "2px solid #F1F5F9",
    },

    acceptButton: {
        position: "absolute",
        right: "1px",
    },

    secondaryContentSecond: {
        display: 'flex',
        alignItems: "flex-start",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignContent: "flex-start",
    },

    ticketInfo1: {
        position: "relative",
        top: 13,
        right: 0,
    },

    Radiusdot: {
        "& .MuiBadge-badge": {
            borderRadius: 2,
            position: "inherit",
            height: 16,
            margin: 2,
            padding: 3,
        },
        "& .MuiBadge-anchorOriginTopRightRectangle": {
            transform: "scale(1) translate(0%, -40%)",
        },
    },

    connectionIcon: {
        marginRight: theme.spacing(0.5),
    },

    presence: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        color: "#0B3C84",
        fontWeight: 600,
        fontSize: "0.8rem",
    },

    actionBtn: {
        padding: 4,
        transition: "all 0.2s ease",
        "&:hover": {
            transform: "scale(1.15)",
        },
    },

    acceptBtn: {
        color: "#0B3C84",
        "&:hover": { backgroundColor: "rgba(11, 60, 132, 0.08)" },
    },

    transferBtn: {
        color: theme.palette.type === "dark" ? "#A78BFA" : "#7C3AED",
        "&:hover": { backgroundColor: "rgba(124, 58, 237, 0.08)" },
    },

    closeBtn: {
        color: theme.palette.type === "dark" ? "#F87171" : "#EF4444",
        "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
    },

    reopenBtn: {
        color: theme.palette.type === "dark" ? "#FBBF24" : "#F59E0B",
        "&:hover": { backgroundColor: "rgba(245, 158, 11, 0.08)" },
    },

    timeAgo: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontSize: "0.65rem",
        color: theme.palette.type === "dark" ? "#64748B" : "#94A3B8",
        fontWeight: 500,
    },
}));

const chipBaseSx = {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: "0.6rem",
    height: "18px",
    fontWeight: 600,
    '& .MuiChip-avatar': {
        width: 14,
        height: 14,
        fontSize: 14,
    },
};

const safeFormat = (dateString, dateFormat) => {
    if (!dateString) return "";
    let date;
    try {
        date = parseISO(dateString);
    } catch {
        return "";
    }
    if (isNaN(date)) return "";
    try {
        return format(date, dateFormat);
    } catch {
        return "";
    }
};

// Funcao global para parse seguro de datas, retornando a data atual se invalida
export const safeParseISO = (dateString) => {
    if (!dateString) return new Date();
    let date;
    try {
        date = parseISO(dateString);
    } catch {
        return new Date();
    }
    if (isNaN(date)) return new Date();
    return date;
};

const TicketListItemCustom = ({ ticket, user, selected }) => {
    const classes = useStyles();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [ticketUser, setTicketUser] = useState(null);
    const [tag, setTag] = useState([]);

    const [whatsAppName, setWhatsAppName] = useState(null);
    const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);

    const presenceMessage = { composing: "Digitando...", recording: "Gravando..." };

    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");

    const isMounted = useRef(true);


    const { get: getSetting } = useCompanySettings();

    useEffect(() => {
        if (ticket.userId && ticket.user) {
            setTicketUser(ticket?.user?.name.toUpperCase());
        }

        setWhatsAppName(ticket?.whatsapp?.name);

        setTag(ticket?.tags);

        return () => {
            isMounted.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenAcceptTicketWithouSelectQueue = React.useCallback(() => {

        setAcceptTicketWithouSelectQueueOpen(true);
    }, []);

    const handleCloseTicket = async (id) => {
        const setting = await getSetting(
            {
                "column": "requiredTag"
            }
        );

        if (setting.requiredTag === "enabled") {
            //verificar se tem uma tag   
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"))
                } else {
                    await api.put(`/tickets/${id}`, {
                        status: "closed",
                        userId: user?.id || null,
                        promptId: null,
                    });

                    if (isMounted.current) {
                        setLoading(false);
                    }
                    history.push(`/tickets/`);
                }
            } catch (err) {
                setLoading(false);
                toastError(err);
            }
        } else {
            setLoading(true);
            try {
                await api.put(`/tickets/${id}`, {
                    status: "closed",
                    userId: user?.id || null,
                });

            } catch (err) {
                setLoading(false);
                toastError(err);
            }
            if (isMounted.current) {
                setLoading(false);
            }
            history.push(`/tickets/`);
        }

    };

    const handleCloseIgnoreTicket = async (id) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${id}`, {
                status: "closed",
                userId: user?.id || null,
                sendFarewellMessage: false,
                amountUsedBotQueues: 0
            });

        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/`);
    };

    const truncate = (str, len) => {
        if (!isNil(str)) {
            if (str.length > len) {
                return str.substring(0, len) + "...";
            }
            return str;
        }
    };

    const handleCloseTransferTicketModal = React.useCallback(() => {
        if (isMounted.current) {
            setTransferTicketModalOpen(false);
        }
    }, []);

    const handleOpenTransferModal = React.useCallback(() => {
        setLoading(true)
        setTransferTicketModalOpen(true);
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    }, [ticket.uuid])

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            const otherTicket = await api.put(`/tickets/${id}`, {
                status: ticket.isGroup && ticket.channel === 'whatsapp' ? "group" : "open",
                userId: user?.id,
            });

            if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    setOpenAlert(true)
                    setUserTicketOpen(otherTicket.data.user.name)
                    setQueueTicketOpen(otherTicket.data.queue.name)
                } else {
                    setLoading(false);

                    history.push(`/tickets/${otherTicket.data.uuid}`);
                }
            } else {
                let setting;

                try {
                    setting = await getSetting({
                        "column": "sendGreetingAccepted"
                    })
                } catch (err) {
                    toastError(err);
                }

                if (setting.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
                    handleSendMessage(ticket.id);
                }
                if (isMounted.current) {
                    setLoading(false);
                }
                history.push(`/tickets/${ticket.uuid}`);
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }

    };

    const renderTime = () => {
        const updatedAt = safeParseISO(ticket?.updatedAt);
        const mins = differenceInMinutes(new Date(), updatedAt);
        const hrs = differenceInHours(new Date(), updatedAt);

        let text;
        if (isYesterday(updatedAt) || hrs >= 24) {
            text = "Ontem";
        } else if (mins >= 60) {
            text = `${hrs}h`;
        } else {
            text = `${mins}min`;
        }

        return (
            <span className={classes.timeAgo}>
                {text}
            </span>
        );
    };



    const handleSendMessage = async (id) => {
        const msg = `{{ms}} *{{name}}*, ${i18n.t("mainDrawer.appBar.user.myName")} *${user?.name}* ${i18n.t("mainDrawer.appBar.user.continuity")}.`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `*${i18n.t("mainDrawer.appBar.user.virtualAssistant")}:*\n${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
        }
    };

    const handleCloseAlert = React.useCallback(() => {
        setOpenAlert(false);
        setLoading(false);
    }, []);

    const handleSelectTicket = (ticket) => {
        history.push(`/tickets/${ticket.uuid}`);
    };

    return (
        <React.Fragment key={ticket.id}>
            <ShowTicketOpen
                isOpen={openAlert}
                handleClose={handleCloseAlert}
                user={userTicketOpen}
                queue={queueTicketOpen}
            />
            <AcceptTicketWithouSelectQueue
                modalOpen={acceptTicketWithouSelectQueueOpen}
                onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
                ticketId={ticket.id}
                ticket={ticket}
            />
            <TransferTicketModalCustom
                modalOpen={transferTicketModalOpen}
                onClose={handleCloseTransferTicketModal}
                ticketid={ticket.id}
                ticket={ticket}
            />

            <ListItem dense button
                onClick={(e) => {
                    handleSelectTicket(ticket);
                }}
                selected={selected}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                })}
            >

                <ListItemAvatar className={classes.avatarContainer}>
                    <Badge
                        className={classes.newMessagesCount}
                        badgeContent={ticket.unreadMessages}
                        classes={{
                            badge: classes.badgeStyle,
                        }}
                    >
                        <Avatar
                            className={classes.avatar}
                            src={`${ticket?.contact?.profilePicUrl}`}
                        />
                    </Badge>
                </ListItemAvatar>
                <ListItemText
                    disableTypography
                    primary={
                        <span className={classes.contactNameWrapper}>
                            <Typography
                                noWrap
                                component="span"
                                variant="body2"
                                className={classes.contactName}
                            >
                                {ticket.isGroup && ticket.channel === "whatsapp" && (
                                    <GroupIcon
                                        fontSize="small"
                                        style={{ color: grey[500], marginBottom: "-2px", marginRight: 4 }}
                                    />
                                )}

                                {ticket.channel && (
                                    <Tooltip title={whatsAppName || "Carregando..."} arrow>
                                        <span style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>
                                            <ConnectionIcon
                                                width="18"
                                                height="18"
                                                className={classes.connectionIcon}
                                                connectionType={ticket.channel}
                                            />
                                        </span>
                                    </Tooltip>
                                )}

                                {truncate(`${ticket.contact?.name} #${ticket.id}`, 30)}
                            </Typography>
                        </span>
                    }
                    secondary={
                        <span className={classes.contactNameWrapper} style={{ flexDirection: "column", alignItems: "flex-start", gap: 2 }}>

                            <Typography
                                className={Number(ticket.unreadMessages) > 0 ? classes.contactLastMessageUnread : classes.contactLastMessage}
                                noWrap
                                component="span"
                                variant="body2"
                            >
                                {["composing", "recording"].includes(ticket?.presence) ? (
                                    <span className={classes.presence}>
                                        {presenceMessage[ticket.presence]}
                                    </span>
                                ) :
                                    <>
                                        {ticket.lastMessage ? (
                                            <>
                                                {ticket.lastMessage.includes('data:image/png;base64') ? <MarkdownWrapper>Localização</MarkdownWrapper> :

                                                    <> {ticket.lastMessage.includes('BEGIN:VCARD') ?
                                                        <MarkdownWrapper>Contato</MarkdownWrapper> :
                                                        <MarkdownWrapper>{truncate(ticket.lastMessage, 30)}</MarkdownWrapper>}
                                                    </>
                                                }
                                            </>
                                        ) : (
                                            <br />
                                        )}

                                    </>
                                }
                            </Typography>

                            <Stack direction="row" spacing={0.5} mt={0.3} flexWrap="wrap">
                                {ticketUser && (
                                    <Chip
                                        sx={{
                                            ...chipBaseSx,
                                            color: "inherit",
                                            borderColor: "rgba(0,0,0,0.15)",
                                            '& .MuiChip-avatar': {
                                                ...chipBaseSx['& .MuiChip-avatar'],
                                                color: 'inherit',
                                            },
                                        }}
                                        avatar={<SupportAgentIcon />}
                                        label={ticketUser}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                <Chip
                                    sx={{
                                        ...chipBaseSx,
                                        backgroundColor: ticket.queue?.color || "#E2E8F0",
                                        // Sem cor de fila (ex.: SEM FILA): fundo claro — texto escuro explícito
                                        // para não herdar cor clara do tema escuro (ilegível sobre #E2E8F0).
                                        color: ticket.queue?.color ? "#FFFFFF" : "#0F172A",
                                        border: "none",
                                        '& .MuiChip-avatar': {
                                            ...chipBaseSx['& .MuiChip-avatar'],
                                            color: ticket.queue?.color ? "#FFFFFF" : "#0F172A",
                                        },
                                    }}
                                    avatar={<BookmarksIcon />}
                                    label={ticket.queue?.name?.toUpperCase() || "SEM FILA"}
                                    variant="filled"
                                    size="small"
                                />
                                {whatsAppName && (
                                    <Chip
                                        sx={{
                                            ...chipBaseSx,
                                            backgroundColor: "#0B3C84",
                                            color: "#FFFFFF",
                                            border: "none",
                                            '& .MuiChip-avatar': {
                                                ...chipBaseSx['& .MuiChip-avatar'],
                                                color: "#FFFFFF",
                                            },
                                        }}
                                        avatar={<LinkIcon />}
                                        label={whatsAppName}
                                        variant="filled"
                                        size="small"
                                    />
                                )}
                            </Stack>

                            {/* Renderizacao das tags em uma nova linha */}
                            {((ticket.contact?.tags && ticket.contact.tags.length > 0) || (tag && tag.length > 0)) && (
                                <Stack direction="row" spacing={0.5} mt={0.3} flexWrap="wrap">
                                    {ticket.contact?.tags?.map(tagItem => (
                                        <Chip
                                            key={`ticket-contact-tag-${ticket.id}-${tagItem.id}`}
                                            sx={{
                                                ...chipBaseSx,
                                                backgroundColor: tagItem.color || "#E2E8F0",
                                                color: "#FFFFFF",
                                                border: "none",
                                                '& .MuiChip-avatar': {
                                                    ...chipBaseSx['& .MuiChip-avatar'],
                                                    color: "#FFFFFF",
                                                },
                                            }}
                                            avatar={<StyleIcon />}
                                            label={tagItem.name}
                                            variant="filled"
                                            size="small"
                                        />
                                    ))}
                                    {tag?.map(tagItem => (
                                        <Chip
                                            key={`ticket-tag-${tagItem.id}`}
                                            sx={{
                                                ...chipBaseSx,
                                                backgroundColor: tagItem.color || "#E2E8F0",
                                                color: tagItem.color ? "#FFFFFF" : "inherit",
                                                border: "none",
                                                '& .MuiChip-avatar': {
                                                    ...chipBaseSx['& .MuiChip-avatar'],
                                                    color: tagItem.color ? "#FFFFFF" : "inherit",
                                                },
                                            }}
                                            avatar={<ViewKanbanIcon />}
                                            label={tagItem.name}
                                            variant="filled"
                                            size="small"
                                        />
                                    ))}
                                </Stack>
                            )}

                        </span>
                    }

                />
                <ListItemSecondaryAction>
                    {ticket.lastMessage && (

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginTop: -20 }}>

                            <Typography
                                className={Number(ticket.unreadMessages) > 0 ? classes.lastMessageTimeUnread : classes.lastMessageTime}
                                component="span"
                                variant="body2"
                            >

                                {isSameDay(safeParseISO(ticket.updatedAt), new Date()) ? (
                                    <>{safeFormat(ticket.updatedAt, "HH:mm")}</>
                                ) : (
                                    <>{safeFormat(ticket.updatedAt, "dd/MM/yyyy")}</>
                                )}

                            </Typography>

                            <Box>{renderTime()}</Box>

                        </Box>
                    )}



                    <span className={classes.secondaryContentSecond}>
                        {(ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                                <IconButton
                                    className={`${classes.actionBtn} ${classes.acceptBtn}`}
                                    size="small"
                                    onClick={e => handleOpenAcceptTicketWithouSelectQueue()}
                                    disabled={loading}
                                    style={{ position: "absolute", right: 51, bottom: -30 }}
                                >
                                    <Done style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond} >
                        {(ticket.status === "pending" && ticket.queueId !== null) && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                                <IconButton
                                    className={`${classes.actionBtn} ${classes.acceptBtn}`}
                                    size="small"
                                    onClick={e => handleAcepptTicket(ticket.id)}
                                    disabled={loading}
                                    style={{ position: "absolute", right: 51, bottom: -30 }}
                                >
                                    <Done style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond} >
                        {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                                <IconButton
                                    className={`${classes.actionBtn} ${classes.transferBtn}`}
                                    size="small"
                                    onClick={handleOpenTransferModal}
                                    disabled={loading}
                                    style={{ position: "absolute", right: 26, bottom: -30 }}
                                >
                                    <SwapHoriz style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond} >
                        {(ticket.status === "open" || ticket.status === "group") && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                                <IconButton
                                    className={`${classes.actionBtn} ${classes.closeBtn}`}
                                    size="small"
                                    onClick={e => handleCloseTicket(ticket.id)}
                                    disabled={loading}
                                    style={{ position: "absolute", right: 1, bottom: -30 }}
                                >
                                    <HighlightOff style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </span>

                    <span className={classes.secondaryContentSecond} >
                        {(ticket.status === "pending") && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                                <IconButton
                                    className={`${classes.actionBtn} ${classes.closeBtn}`}
                                    size="small"
                                    onClick={e => handleCloseIgnoreTicket(ticket.id)}
                                    disabled={loading}
                                    style={{ position: "absolute", right: 1, bottom: -30 }}
                                >
                                    <HighlightOff style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </span>

                    <span className={classes.secondaryContentSecond} >
                        {(ticket.status === "closed" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                                <IconButton
                                    className={`${classes.actionBtn} ${classes.reopenBtn}`}
                                    size="small"
                                    onClick={e => handleOpenAcceptTicketWithouSelectQueue()}
                                    disabled={loading}
                                    style={{ position: "absolute", right: 1, bottom: -30 }}
                                >
                                    <Replay style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </span>

                    <span className={classes.secondaryContentSecond} >
                        {(ticket.status === "closed" && ticket.queueId !== null) && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                                <IconButton
                                    className={`${classes.actionBtn} ${classes.reopenBtn}`}
                                    size="small"
                                    onClick={e => handleAcepptTicket(ticket.id)}
                                    disabled={loading}
                                    style={{ position: "absolute", right: 1, bottom: -30 }}
                                >
                                    <Replay style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </span>
                </ListItemSecondaryAction>
                <ListItemSecondaryAction>

                </ListItemSecondaryAction>
            </ListItem>
        </React.Fragment>
    );
};

const arePropsEqual = (prevProps, nextProps) => {
    if (prevProps.selected !== nextProps.selected) return false;
    if (prevProps.user !== nextProps.user) return false;

    const prevT = prevProps.ticket;
    const nextT = nextProps.ticket;
    if (prevT === nextT) return true;

    return (
        prevT.id === nextT.id &&
        prevT.lastMessage === nextT.lastMessage &&
        prevT.unreadMessages === nextT.unreadMessages &&
        prevT.status === nextT.status &&
        prevT.updatedAt === nextT.updatedAt &&
        prevT.userId === nextT.userId &&
        prevT.queueId === nextT.queueId &&
        prevT.presence === nextT.presence &&
        prevT.contact?.name === nextT.contact?.name &&
        prevT.contact?.profilePicUrl === nextT.contact?.profilePicUrl &&
        prevT.contact?.presence === nextT.contact?.presence &&
        prevT.user?.name === nextT.user?.name &&
        prevT.whatsapp?.name === nextT.whatsapp?.name
    );
};

export default React.memo(TicketListItemCustom, arePropsEqual);
